import { MODULE } from "./constants.mjs";
import { registerSettings } from "./settings.mjs";
import * as migration from "./migration.mjs";
import { Utils } from "./utils.mjs";
import { ChangeLog } from "./change-log.mjs";

/**
 * Initialize the module, register settings, and expose API.
 */
Hooks.on("init", () => {
  registerSettings();
  migration.register();
  game.changeLog = new ChangeLog();
  game.changeLog.init();

  const module = game.modules.get(MODULE.ID);
  module.api = {
    undo,
    Utils
  };
});

/* -------------------------------------------- */

/**
 * Override chat notifications to suppress them for change log messages the user should not see.
 */
Hooks.on("ready", async () => {
  await migration.migrate();

  const original = ui.chat.notify.bind(ui.chat);
  ui.chat.notify = (message, options) => {
    const changeLogFlags = message.flags?.["change-log"];
    const isChangeLog = changeLogFlags?.key || changeLogFlags?.changes;
    if ( isChangeLog && message.whisper.length && !message.whisper.includes(game.user.id) ) {
      return;
    }
    return original(message, options);
  };

  game.socket.on(`module.${MODULE.ID}`, async data => {
    if ( !game.user.isGM ) return;
    if ( data.action === "logToJournal" ) {
      await ChangeLog.writeToJournal(data.journalName, data.html, data.ownership);
    }
  });
});

/* -------------------------------------------- */

/**
 * Register the Change Log tab with Custom Chat Tabs.
 */
Hooks.on("custom-chat-tabs.init", () => {
  if ( Utils.getSetting("chatTabVisibility") === "none" ) return;

  const api = game.modules.get("custom-chat-tabs")?.api;
  if ( !api ) return;

  api.register({
    key: "change-log",
    label: "Change Log",
    icon: "fas fa-notebook",
    exclusive: Utils.getSetting("chatTabVisibility") === "exclusive",
    filter: message => !!message.flags?.["change-log"],
    ...(game.changeLog.chatTabRoles !== undefined && { roles: game.changeLog.chatTabRoles })
  });
});

/* -------------------------------------------- */

/**
 * Add "Undo Change" context menu item to chat log entries.
 * @param {HTML} html The chat log HTML.
 * @param {Array} menuItems The context menu items.
 */
Hooks.on("getChatMessageContextOptions", async (html, menuItems) => {
  const menuItem = {
    name: game.i18n.localize("changeLog.undo"),
    icon: '<i class="fa-solid fa-rotate-left"></i>',
    condition: li => {
      if ( !li.dataset.messageId ) return false;
      const message = game.messages.get(li.dataset.messageId);
      const changeLogFlags = message.flags?.["change-log"];
      if ( !changeLogFlags ) return false;

      if ( changeLogFlags.changes ) {
        return changeLogFlags.changes.some(c => (c.val !== null && c.val !== undefined) || c.entityData);
      }

      return (changeLogFlags.val !== null && changeLogFlags.val !== undefined) || !!changeLogFlags.entityData;
    },
    callback: li => {
      undo(li.dataset.messageId);
    }
  };

  menuItems.push(menuItem);
});

/* -------------------------------------------- */

/**
 * Handle item creation for change log.
 * @param {Item} item
 * @param {object} options
 * @param {string} userId
 */
Hooks.on("createItem", async (item, options, userId) => {
  await game.changeLog.getCreateItem({ item, options, userId });
});

/* -------------------------------------------- */

/**
 * Handle item deletion for change log.
 * @param {Item} item
 * @param {object} options
 * @param {string} userId
 */
Hooks.on("deleteItem", async (item, options, userId) => {
  await game.changeLog.getDeleteItem({ item, options, userId });
});

/* -------------------------------------------- */

/**
 * Handle pre-creation of combatant for change log.
 * @param {Combatant} combatant
 * @param {object} data
 * @param {object} options
 * @param {string} userId
 */
Hooks.on("preCreateCombatant", async (combatant, data, options, userId) => {
  await game.changeLog.getPreCreateCombatant({ combatant, data, options, userId });
});

/* -------------------------------------------- */

/**
 * Handle pre-deletion of active effect for change log.
 * @param {ActiveEffect} activeEffect
 * @param {object} data
 * @param {string} userId
 */
Hooks.on("preDeleteActiveEffect", async (activeEffect, data, userId) => {
  await game.changeLog.getDeleteData("activeEffect", { doc: activeEffect, data, userId });
});

/* -------------------------------------------- */

/**
 * Handle pre-update of active effect for change log.
 * @param {ActiveEffect} activeEffect
 * @param {object} data
 * @param {object} options
 * @param {string} userId
 */
Hooks.on("preUpdateActiveEffect", async (activeEffect, data, options, userId) => {
  await game.changeLog.getPreUpdateData("activeEffect", { doc: activeEffect, data, options, userId });
});

/* -------------------------------------------- */

/**
 * Handle pre-update of actor for change log.
 * @param {Actor} actor
 * @param {object} data
 * @param {object} options
 * @param {string} userId
 */
Hooks.on("preUpdateActor", async (actor, data, options, userId) => {
  await game.changeLog.getPreUpdateData("actor", { doc: actor, data, options, userId });
});

/* -------------------------------------------- */

/**
 * Handle pre-update of item for change log.
 * @param {Item} item
 * @param {object} data
 * @param {object} options
 * @param {string} userId
 */
Hooks.on("preUpdateItem", async (item, data, options, userId) => {
  await game.changeLog.getPreUpdateData("item", { doc: item, data, options, userId });
});

/* -------------------------------------------- */

/**
 * Handle pre-update of token for change log.
 * @param {Token} token
 * @param {object} data
 * @param {object} options
 * @param {string} userId
 */
Hooks.on("preUpdateToken", async (token, data, options, userId) => {
  await game.changeLog.getPreUpdateData("token", { doc: token, data, options, userId });
});

/* -------------------------------------------- */

/**
 * Handle actor update for change log.
 * @param {Actor} actor
 * @param {object} data
 * @param {object} options
 * @param {string} userId
 */
Hooks.on("updateActor", async (actor, data, options, userId) => {
  if ( game.userId !== data._stats?.lastModifiedBy ) return;
  await game.changeLog.getUpdateData("actor", { doc: actor, data, options, userId });
});

/* -------------------------------------------- */

/**
 * Handle item update for change log.
 * @param {Item} item
 * @param {object} data
 * @param {object} options
 * @param {string} userId
 */
Hooks.on("updateItem", async (item, data, options, userId) => {
  if ( game.userId !== data._stats?.lastModifiedBy ) return;
  await game.changeLog.getUpdateData("item", { doc: item, data, options, userId });
});

/* -------------------------------------------- */

/**
 * Render chat message for change log, hiding or modifying elements as needed.
 * @param {ChatMessage} chatMessage
 * @param {JQuery} html
 */
Hooks.on("renderChatMessageHTML", async (chatMessage, html, options) => {
  const changeLogFlags = chatMessage.flags?.["change-log"];
  if ( !changeLogFlags?.key && !changeLogFlags?.changes ) return;
  if ( chatMessage.whisper.length && !chatMessage.whisper.includes(game.user.id) ) { html.style.display = "none"; }
  if ( !game.changeLog.showRecipients ) { html.querySelector(".whisper-to")?.remove(); }
  if ( !game.changeLog.showSender ) {
    html.querySelector(".message-sender")?.remove();
    html.style.position = "relative";
    const messageHeader = html.querySelector(".message-header");
    if ( messageHeader ) {
      messageHeader.style.position = "absolute";
      messageHeader.style.right = "6px";
    }
  }
});

/* -------------------------------------------- */

/**
 * Render DnD5e chat message for change log, hiding recipients if needed.
 * @param {ChatMessage} chatMessage
 * @param {HTMLElement} html
 */
Hooks.on("dnd5e.renderChatMessage", async (chatMessage, html) => {
  const changeLogFlags = chatMessage.flags?.["change-log"];
  if ( !changeLogFlags?.key && !changeLogFlags?.changes ) return;
  if ( !game.changeLog.showRecipients ) { html.querySelector("span.subtitle")?.remove(); }
});

/* -------------------------------------------- */

/**
 * Undo a change based on a chat message ID.
 * @param {string} chatMessageId The ID of the chat message to undo.
 * @returns {Promise<void>}
 */
async function undo(chatMessageId) {
  const chatMessage = game.messages.get(chatMessageId);
  if ( !chatMessage || !chatMessage.flags || !chatMessage.flags["change-log"] ) return;

  const changeLogFlags = chatMessage.flags["change-log"];

  if ( changeLogFlags.changes ) {
    const changes = [...changeLogFlags.changes].reverse();
    for ( const change of changes ) {
      await undoSingleChange(change);
    }
    return;
  }

  await undoSingleChange(changeLogFlags);
}

/* -------------------------------------------- */

/**
 * Undo a single change entry.
 * @param {object} changeData The change data.
 * @param {string} changeData.tokenId The token ID.
 * @param {string} changeData.actorId The actor ID.
 * @param {string} changeData.id The document ID.
 * @param {string} changeData.key The property key.
 * @param {string} changeData.type The document type.
 * @param {*} changeData.val The original value.
 * @param {object} changeData.entityData The entity data.
 * @returns {Promise<void>}
 */
async function undoSingleChange({ tokenId, actorId, id, key, type, val, entityData }) {
  if ( (!id || val === null) && !entityData ) return;

  const token = (tokenId)
    ? game.scenes.map(scene => scene.tokens.find(token => token.id === tokenId)).filter(token => !!token)[0]
    : null;
  const actor = (token) ? token.actor : (actorId) ? game.actors.get(actorId) : null;

  if ( entityData ) {
    actor.createEmbeddedDocuments("Item", [entityData]);
    return;
  }

  let doc;
  switch (type) {
    case "actor":
      doc = actor;
      break;
    case "item":
      doc = (actor) ? actor?.items.get(id) : game.items.get(id);
      break;
    case "token":
      doc = token;
      break;
  }

  if ( !doc ) return;

  let updateKey = key;
  let updateValue = val;
  const dataType = Utils.getTypeByDotNotation(doc, key);

  if ( dataType === "array" || dataType === "set" ) {
    const arrayValue = key.split(".").pop();
    updateKey = key.split(".").slice(0, -1).join(".");
    const arr = Array.from(Utils.getValueByDotNotation(doc, updateKey));
    if ( val ) {
      arr.push(arrayValue);
    } else {
      const index = arr.findIndex(a => a === arrayValue);
      arr.splice(index, 1);
    }
    updateValue = arr;
  }

  await doc.update({ [updateKey]: updateValue });
}
